// src/pages/ClientCreatePage.tsx — Nexus-themed new-client form
// Replaces admin/src/pages/ClientCreatePage.tsx
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import type {
  CreateClientRequest,
  CreateClientResponse,
} from '@eotir/oidcbridge-shared/api-contracts';
import { GroupPicker } from '../components/group-picker';
import { GroupBadge } from '../components/group-badge';
import { GroupRefresh } from '../components/group-refresh';
import { DEFAULT_GROUP_MAP } from '@eotir/oidcbridge-shared/forum-groups';
import { ChevronDown } from 'lucide-react';

export function ClientCreatePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [clientId, setClientId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [downstreamMode, setDownstreamMode] = useState<'cloudflare-access-shared' | 'direct-oidc'>('direct-oidc');
  const [redirectUrisText, setRedirectUrisText] = useState('');
  const [groupMode, setGroupMode] = useState<'allow' | 'deny'>('allow');
  const [allowedGroups, setAllowedGroups] = useState<string[]>(['4']);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Deny mode admits every unlisted (and future) group — when switching,
  // seed Guests so a fresh deny-list can't silently open the client to
  // logged-out members.
  const switchMode = (mode: 'allow' | 'deny') => {
    setGroupMode(mode);
    if (mode === 'deny') setAllowedGroups((prev) => (prev.includes('2') ? prev : ['2', ...prev.filter((g) => g !== '4')]));
    if (mode === 'allow') setAllowedGroups((prev) => (prev.length > 0 ? prev : ['4']));
  };

  const [created, setCreated] = useState<CreateClientResponse | null>(null);

  const mut = useMutation({
    mutationFn: (body: CreateClientRequest) =>
      api<CreateClientResponse>('/api/clients', { method: 'POST', body }),
    onSuccess: (res) => { setCreated(res); qc.invalidateQueries({ queryKey: ['clients'] }); },
  });

  // Fetch group map for GroupPicker and GroupBadge display
  const { data: groupData, isLoading: groupsLoading } = useQuery({
    queryKey: ['forum-groups'],
    queryFn: ({ signal }) =>
      api<{ groupMap: Record<string, string>; source: string; lastRefreshedAt?: string }>(
        '/api/forum-groups',
        { signal },
      ),
    staleTime: 5 * 60_000,
  });
  const groupMap = groupData?.groupMap ?? DEFAULT_GROUP_MAP;

  function removeGroup(id: string) {
    setAllowedGroups((prev) => prev.filter((g) => g !== id));
  }

  if (created) {
    return (
      <div className="max-w-3xl">
        <div className="eo-eyebrow">Client Registered</div>
        <h1 className="eo-h1 mb-4">Created <span className="font-mono text-crimson-hover">{created.client.clientId}</span></h1>
        <div className="border border-warn bg-warn/10 p-4 rounded mb-4">
          <p className="font-mono text-[10px] tracking-label uppercase text-warn-soft mb-2">
            One-time client secret — copy now. Will not be shown again.
          </p>
          <code className="block font-mono text-sm break-all text-ink">{created.secret}</code>
        </div>
        <div className="flex gap-3">
          <Link to={`/clients/${created.client.clientId}`} className="eo-btn">View Details</Link>
          <button type="button" onClick={() => navigate('/')} className="eo-btn eo-btn--ghost">Back to clients</button>
        </div>
      </div>
    );
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const redirectUris = redirectUrisText.split('\n').map((s) => s.trim()).filter(Boolean);
    const allowedGroupsNumber = allowedGroups.map((id) => parseInt(id, 10)).filter((n) => Number.isFinite(n) && n > 0);
    // displayName persists to KV; label is the registry-only mirror so the
    // audit log + drift snapshot keep seeing the same value.
    mut.mutate({
      clientId: clientId.trim(),
      displayName: displayName.trim(),
      label: displayName.trim(),
      downstreamMode,
      redirectUris,
      allowedGroups: allowedGroupsNumber,
      groupMode,
    });
  };

  const apiErr = mut.error as ApiError | undefined;
  const fieldErrors = apiErr?.fieldErrors ?? {};

  return (
    <div className="max-w-3xl">
      <div className="eo-eyebrow">Register New Client</div>
      <h1 className="eo-h1 mb-5">New Client</h1>
      <form onSubmit={submit} className="eo-card space-y-4">
        <Field label="Client ID" hint="lowercase, digits, hyphens; must start and end with alphanumeric" error={fieldErrors.clientId}>
          <input type="text" value={clientId} onChange={(e) => setClientId(e.target.value)}
                 placeholder="e.g. name-generator" className="eo-input" required />
        </Field>
        <Field label="Display name" hint="Human-readable name shown throughout the admin (editable later)" error={fieldErrors.displayName ?? fieldErrors.label}>
          <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                 placeholder="e.g. Name Generator (prod)" className="eo-input" required />
        </Field>
        <Field label="Downstream mode" error={fieldErrors.downstreamMode}>
          <select value={downstreamMode}
                  onChange={(e) => setDownstreamMode(e.target.value as 'cloudflare-access-shared' | 'direct-oidc')}
                  className="eo-select w-72">
            <option value="direct-oidc">direct-oidc</option>
            <option value="cloudflare-access-shared">cloudflare-access-shared</option>
          </select>
        </Field>
        <Field label="Redirect URIs" hint="One per line; absolute http/https URLs, no fragments" error={fieldErrors.redirectUris}>
          <textarea value={redirectUrisText} onChange={(e) => setRedirectUrisText(e.target.value)}
                    rows={3} placeholder="https://app.eotir.com/auth/callback"
                    className="eo-textarea" required />
        </Field>
        <Field label="Group policy" error={fieldErrors.groupMode}>
          <select value={groupMode}
                  onChange={(e) => switchMode(e.target.value as 'allow' | 'deny')}
                  className="eo-select w-72">
            <option value="allow">Only allow these groups</option>
            <option value="deny">All groups except…</option>
          </select>
          {groupMode === 'deny' && (
            <p className="eo-hint text-warn-soft">
              Deny mode admits every group not listed — including groups created in the
              future. Keep Guests and other non-member groups in the exclusion list.
            </p>
          )}
        </Field>
        <Field
          label={groupMode === 'deny' ? 'Denied groups' : 'Allowed groups'}
          hint={groupMode === 'deny'
            ? 'Select forum groups that are blocked from this client'
            : 'Select forum groups that can access this client'}
          error={fieldErrors.allowedGroups}
        >
          <GroupRefresh />
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="eo-input w-full flex items-center justify-between bg-bg-2 border border-line text-ink px-2.5 py-1.5 font-mono text-[13px] rounded cursor-pointer"
          >
            <span>
              {allowedGroups.length > 0
                ? `${allowedGroups.length} group${allowedGroups.length !== 1 ? 's' : ''} selected`
                : 'Choose Groups…'}
            </span>
            <ChevronDown size={14} className="text-ink-3" />
          </button>
          {allowedGroups.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {allowedGroups.map((groupId) => (
                <GroupBadge
                  key={groupId}
                  groupId={groupId}
                  groupName={groupMap[groupId] ?? `Group ${groupId}`}
                  variant="default"
                  onRemove={() => removeGroup(groupId)}
                />
              ))}
            </div>
          )}
          <GroupPicker
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            selectedGroups={allowedGroups}
            onSelectionChange={setAllowedGroups}
            groupMap={groupMap}
            isLoading={groupsLoading}
          />
        </Field>

        {apiErr && !Object.keys(fieldErrors).length && (
          <p className="text-sm text-err">{apiErr.message}</p>
        )}

        <div className="flex gap-3 pt-2">
          <button type="submit" disabled={mut.isPending} className="eo-btn">
            {mut.isPending ? 'Creating…' : 'Create'}
          </button>
          <button type="button" onClick={() => navigate('/')} className="eo-btn eo-btn--ghost">Cancel</button>
        </div>
      </form>
    </div>
  );
}

function Field(props: { label: string; hint?: string; error?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="eo-label">{props.label}</span>
      {props.children}
      {props.hint && !props.error && <p className="eo-hint">{props.hint}</p>}
      {props.error && <p className="text-[11px] text-err mt-1.5 font-mono">{props.error}</p>}
    </label>
  );
}
